import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileCheck, HeadphonesIcon, Sparkles, Zap } from "lucide-react";

const MPR_LOGO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_efficiency-certified/artifacts/ne6txy5q_maprimerenov-gouv-fr-2020.png";

/**
 * ParticuliersSpotlight — Section Home dédiée aux particuliers.
 * Met en avant le statut "Mandataire officiel MaPrimeRénov' & CEE".
 */
export default function ParticuliersSpotlight() {
  return (
    <section
      data-testid="particuliers-spotlight"
      className="relative overflow-hidden bg-white text-slate-900 border-t border-slate-100"
    >
      {/* Fond MPR style : subtile teinte bleu-vert */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#F0F7FF]/60 to-[#ECFDF5]/60" />
      {/* Ambient orbs — sobres institutionnelles */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#0055A4]/[0.08] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#10B981]/[0.08] blur-3xl" />
      {/* Grid pattern subtil */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#0055A4 1px, transparent 1px), linear-gradient(90deg, #0055A4 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Logo officiel — column left */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              {/* Halo */}
              <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-[#0055A4]/20 via-transparent to-[#10B981]/20 blur-2xl" />
              <div
                className="relative rounded-3xl bg-white p-6 sm:p-8 border border-slate-200 shadow-lg"
                data-testid="particuliers-spotlight-logo-card"
              >
                <img
                  src={MPR_LOGO}
                  alt="MaPrimeRénov' — République Française — Agence Nationale de l'Habitat"
                  className="w-full max-w-[440px] mx-auto h-auto object-contain"
                  loading="lazy"
                  data-testid="particuliers-spotlight-logo"
                />
              </div>

              {/* Floating credentials chips — sobres institutionnels */}
              <div className="absolute -top-4 -right-4 rounded-full bg-[#0055A4] px-4 py-2 shadow-xl border border-white/10">
                <div className="flex items-center gap-2 text-white text-xs font-bold tracking-wide">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span>MANDATAIRE OFFICIEL</span>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 rounded-full bg-[#10B981] px-4 py-2 shadow-xl hidden sm:flex border border-white/10">
                <div className="flex items-center gap-2 text-white text-xs font-bold tracking-wide">
                  <Zap className="h-3.5 w-3.5" />
                  <span>MPR · ANAH · CEE</span>
                </div>
              </div>
            </div>
          </div>

          {/* Copy — column right */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#0055A4]/25 bg-[#0055A4]/[0.06] px-3 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-[#0055A4]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#0055A4] font-bold">
                Espace Particuliers
              </span>
            </div>

            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] mb-6 text-slate-900">
              ECONATION,
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0055A4] via-[#0369A1] to-[#10B981]">
                mandataire officiel MPR, Anah &amp; CEE
              </span>
            </h2>

            <p className="text-slate-600 text-lg leading-relaxed max-w-2xl mb-8">
              Nous sommes votre <strong className="text-slate-900">mandataire officiel</strong> auprès
              de l&apos;<strong className="text-slate-900">Agence Nationale de l&apos;Habitat (Anah)</strong> et des obligés énergéticiens.
              Nous constituons, déposons et suivons vos dossiers <strong className="text-slate-900">MaPrimeRénov&apos; (MPR)</strong> et
              <strong className="text-slate-900"> Certificats d&apos;Économies d&apos;Énergie (CEE)</strong>. Vous n&apos;avez rien à faire.
            </p>

            {/* Credentials grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10 max-w-2xl">
              {[
                { icon: FileCheck, label: "Constitution du dossier", desc: "Pièces & formulaires" },
                { icon: ShieldCheck, label: "Dépôt à l'Anah", desc: "En votre nom" },
                { icon: HeadphonesIcon, label: "Suivi jusqu'au versement", desc: "Sans stress" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:border-[#0055A4]/40 transition"
                >
                  <div className="h-9 w-9 rounded-lg bg-[#0055A4]/10 text-[#0055A4] grid place-items-center mb-2.5">
                    <item.icon className="h-4 w-4" />
                  </div>
                  <div className="text-slate-900 text-sm font-semibold">{item.label}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{item.desc}</div>
                </div>
              ))}
            </div>

            {/* CTAs — Institutional palette */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/simulateur-pac"
                data-testid="particuliers-spotlight-cta-simulator"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#0055A4] hover:bg-[#003D7A] px-7 py-4 text-white text-sm font-semibold tracking-wide transition shadow-[0_10px_30px_-10px_rgba(0,85,164,0.6)] hover:-translate-y-0.5"
              >
                Vérifier mes aides PAC
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/particuliers"
                data-testid="particuliers-spotlight-cta-particuliers"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-300 hover:border-slate-500 bg-white text-slate-900 px-7 py-4 text-sm font-semibold tracking-wide transition"
              >
                Découvrir l&apos;espace Particuliers
              </Link>
            </div>

            <div className="mt-6 flex items-center gap-2 text-slate-500 text-[11px]">
              <ShieldCheck className="h-3.5 w-3.5 text-[#10B981]" />
              <span>Mandataire enregistré · Traitement RGPD · Estimation indicative avant étude complète</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
